import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Coins, Scale, ScrollText, Store, Play, Sparkles, ShieldCheck, Building2, ChevronDown, Mail, FileText, HelpCircle, FolderArchive } from 'lucide-react';
import { useZakatGlobal } from '../context/ZakatGlobalContext';

// Moved out of the parent render so React doesn't reconcile a brand-new
// component type on every re-render.
const MenuItem = ({ icon: Icon, label, onClick, testid, active, tone }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-current={active ? 'page' : undefined}
    className={`w-full flex items-center gap-2.5 py-2 pl-3 pr-2.5 rounded-md text-left text-[14px] font-medium transition-colors ${
      active
        ? 'bg-[#F3EFEA] text-[#0D1520] border-l-[3px] border-[#0D4733]'
        : tone === 'gold'
          ? 'text-[#0D4733] hover:bg-[#F3EFEA] hover:text-[#0D4733]'
          : 'text-[#0D1520] hover:bg-[#F3EFEA] hover:text-[#0D1520]'
    }`}
  >
    {Icon && <Icon className="w-4 h-4 shrink-0 text-[#0D4733]" />}
    <span className="flex-1">{label}</span>
  </button>
);

const SectionHeader = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#4A5568] px-2 pt-2 pb-1">
    {children}
  </p>
);

/**
 * DesktopMoreMenu
 *
 * A compact "More" menu for the desktop top nav. Opens a small popover
 * with the full site map — mirrors the mobile nav's content set — so
 * links never get hidden purely by viewport width.
 */
export const DesktopMoreMenu = ({ scrollToFaq }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useZakatGlobal();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Click-outside + Escape to close
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = (path) => { navigate(path); setOpen(false); };
  const scroll = (fn) => { fn?.(); setOpen(false); };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="nav-more-menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors px-2 py-1 rounded-md ${
          open ? 'text-[#0D1520] bg-[#F3EFEA]' : 'text-[#0D1520] hover:text-[#0D1520] hover:bg-[#F3EFEA]'
        }`}
      >
        {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        <span>{t('v2.nav.menu')}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          data-testid="nav-more-menu-panel"
          className="absolute right-0 mt-2 w-64 rounded-xl bg-white border border-[#E2DCD5] shadow-[0_8px_24px_rgba(43,36,25,0.10)] py-2 z-50"
        >
          <SectionHeader>{t('v2.menu.section.calculators')}</SectionHeader>
          <div className="px-1">
            <MenuItem testid="menu-zakat"        icon={Coins}      label={t('v2.menu.item.zakat')}        onClick={() => go('/')} tone="gold" active={pathname === '/'} />
            <MenuItem testid="menu-inheritance"  icon={Scale}      label={t('v2.menu.item.inheritance')}  onClick={() => go('/inheritance')} tone="gold" active={pathname.startsWith('/inheritance')} />
            <MenuItem testid="menu-will"         icon={ScrollText} label={t('v2.menu.item.will')}         onClick={() => go('/will')} tone="gold" active={pathname.startsWith('/will')} />
            <MenuItem testid="menu-succession"   icon={FolderArchive} label={t('v2.menu.item.succession')} onClick={() => go('/succession')} tone="gold" active={pathname.startsWith('/succession')} />
            <MenuItem testid="menu-business"     icon={Store}      label={t('v2.menu.item.business')}     onClick={() => go('/business-calculator')} tone="gold" active={pathname.startsWith('/business-calculator')} />
          </div>

          <div className="my-1.5 mx-3 border-t border-[#E2DCD5]" />

          <SectionHeader>{t('v2.menu.section.lifeRefs')}</SectionHeader>
          <div className="px-1">
            <MenuItem testid="menu-life"        icon={Sparkles}    label={t('v2.menu.item.life')}        onClick={() => go('/life')} active={pathname.startsWith('/life')} />
            <MenuItem testid="menu-walkthrough" icon={Play}        label={t('v2.menu.item.walkthrough')} onClick={() => go('/walkthrough')} active={pathname.startsWith('/walkthrough')} />
            <MenuItem testid="menu-masjid"      icon={Building2}   label={t('v2.menu.item.masjid')}      onClick={() => go('/masjid-resources')} active={pathname.startsWith('/masjid-resources')} />
            <MenuItem testid="menu-contact"     icon={Mail}        label={t('v2.menu.item.contact')}     onClick={() => go('/contact')} active={pathname.startsWith('/contact')} />
            <MenuItem testid="menu-privacy"     icon={ShieldCheck} label={t('v2.menu.item.privacy')}     onClick={() => go('/privacy')} active={pathname.startsWith('/privacy')} />
          </div>

          <div className="my-1.5 mx-3 border-t border-[#E2DCD5]" />

          <SectionHeader>{t('v2.menu.section.reading')}</SectionHeader>
          <div className="px-1 pb-1">
            <MenuItem testid="menu-articles" icon={FileText}   label={t('v2.menu.item.articles')} onClick={() => go('/articles')} active={pathname.startsWith('/articles')} />
            {scrollToFaq && (
              <MenuItem testid="menu-faq"    icon={HelpCircle} label={t('v2.menu.item.faq')}      onClick={() => scroll(scrollToFaq)} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopMoreMenu;

